import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription, Subject, BehaviorSubject } from 'rxjs';
import Project from 'models/project/project.model';
import TicketAttribute from 'models/ticket-attribute/ticket-attribute.model';
import Ticket from 'models/ticket/ticket.model';
import TicketAttributeValue from 'models/ticket-attribute-value/ticket-attribute-value.model';
import ExternalLayer from 'models/external-layer/external-layer.model';
import ExternalFeature from 'models/external-feature/external-feature.model';
import { ArcgisMapService } from 'services/arcgis-map/arcgis-map.service';
import { TicketAttributeService } from 'services/ticket-attribute/ticket-attribute.service';
import { TicketService } from 'services/ticket/ticket.service';
import { TicketCategoryService } from 'services/ticket-category/ticket-category.service';
import TicketCategory from 'models/ticket-category/ticket-category.model';

@Component({
  selector: 'app-create-ticket',
  templateUrl: './create-ticket.component.html',
  styleUrls: ['./create-ticket.component.scss'],
})
export class CreateTicketComponent implements OnInit, OnDestroy {
  public project: Project = new Project();
  public ticket: Ticket = new Ticket();

  public stage: 'categories' | 'questions' | 'comments' | 'attachments' = 'categories';
  public currentIndex = 0;
  public skipping = false;
  public unSkippable = false;

  public stepper = {
    categories: {
      next: () => this.stage = 'questions',
      cancel: false,
      skip: false,
      done: false,
      forward: false,
    },
    questions: {
      next: () => this.stage = 'comments',
      cancel: false,
      skip: true,
      done: false,
      forward: false,
    },
    comments: {
      next: () => this.stage = 'attachments',
      cancel: true,
      skip: false,
      done: true,
      forward: true,
    },
    attachments: {
      cancel: true,
      skip: false,
      done: true,
      forward: false,
      next: () => {},
    },
  };
  
  public description = '';
  public saved = false;
  public ticketCategories: TicketCategory[] = [];
  public ticketAttributes: TicketAttribute[] = [];
  public ticketAttributesSkip: TicketAttribute[] = [];
  public ticketAttributesSkipped: TicketAttribute[] = [];
  public resolve: any = () => {};

  public layerListSubject: BehaviorSubject<ExternalLayer[]> = new BehaviorSubject([]);
  public clickedFeature: BehaviorSubject<ExternalFeature> = new BehaviorSubject(new ExternalFeature());
  public attributeFilledSubject: Subject<TicketAttributeValue> = new Subject();
  public feature = null;
  
  private ticketAttributeSubscription: Subscription;
  private ticketAttributeValuesSubscription: Subscription;

  constructor(
    public ticketAttributeService: TicketAttributeService,
    public ticketService: TicketService,
    public snackBar: MatSnackBar,
    public arcgisMapService: ArcgisMapService,
    private ticketCategoryService: TicketCategoryService,
    private dialogRef: MatDialogRef<CreateTicketComponent>,
    @Inject(MAT_DIALOG_DATA) data,
  ) {
    this.project = new Project(data.project);
    this.ticket.position = data.position;
    this.ticket.project = data.project;
    this.feature = data.feature ? data.feature.graphic.attributes : null;
    if (data.clicked && data.clicked.objectId.toString().length > 0) {
      this.ticket.externalFeatures = [data.clicked];
      this.feature = data.clicked;
    }
  }

  go() {
    this.resolve();
    this.ticketService.updateDescription(this.ticket);
  }

  done() {
    this.ticketService.updateDescription(this.ticket);
    this.cancel(true);
  }

  async ngOnInit() {
    await this.ticketAttributeService.selectList(this.project);
    await this.layerListSubject.next(this.project.externalLayers);
    await this.ticketCategoryService.list(this.project).then((categories) => {
      this.ticketCategories = categories;
    });
    this.ticketAttributeSubscription = await this.ticketAttributeService.getSelectedList().subscribe((attr) => {
      this.ticketAttributes = attr;
      this.ticketAttributesSkip = attr.filter((att) => !att.otherEnabled);
      this.ticketAttributesSkipped = attr.filter((att) => att.otherEnabled);
      if (this.ticketAttributesSkipped.length === 0) {
        this.unSkippable = true;
      }
    });

    await this.fill();

    this.arcgisMapService.enableSelection();
    this.arcgisMapService.getClickedPosition().subscribe((position) => this.ticket.position = position);

  }

  async fill() {
    await this.handleCategories();

    this.stepper[this.stage].next();

    await this.handleQuestions();

    await this.save();

    this.stepper[this.stage].next();    

    await this.handleComments();

    this.stepper[this.stage].next();
  }

  async handleCategories() {
    await new Promise((resolve) => {
      this.resolve = resolve;
      if (this.ticketCategories.length === 1) {
        this.addCategory(this.ticketCategories[0]);
      }
    });
  }

  async handleQuestions() {
    await new Promise((resolve) => {
      if (this.ticketAttributes.length === 0) {
        resolve(undefined);
      }

      this.resolve = resolve;

      this.ticketAttributeValuesSubscription = this.attributeFilledSubject.subscribe(async (attrValues) => {
        this.ticket.ticketAttributeValues = this.ticket.ticketAttributeValues.filter((attr) => attr.ticketAttribute.id !== attrValues.ticketAttribute.id);
        this.ticket.ticketAttributeValues.push(attrValues);
        this.currentIndex++;

        if (this.skipping ? this.currentIndex === this.ticketAttributesSkip.length : this.currentIndex === this.ticketAttributes.length) {
          this.resolve();
        }
      });
    });
  }

  async handleComments() {
    await new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  addCategory(item: TicketCategory) {
    this.ticket.ticketCategory = item;
    this.resolve();
  }

  skip() {
    this.ticketAttributesSkipped.forEach((item) => this.ticket.ticketAttributeValues.push(new TicketAttributeValue({
      ticketAttribute: item,
      value: '',
    })));

    this.skipping = true;

    if (this.ticketAttributesSkip.length === 0) {
      this.resolve();
    }
  }

  async save() {
    let txt = 'Létrehozás sikeres';

    try {
      await this.ticketService.create(this.ticket).then((ticket) => this.ticket = ticket);
      this.saved = true;
    } catch (ex) {
      console.error(ex);
      txt = 'Létrehozás sikertelen.';
    } finally {
      this.snackBar.open(txt, '', {
        duration: 2000,
      });
      this.arcgisMapService.disableSelection();
    }
  }

  setComment(value: string) {
    this.description = value;
  }

  cancel(refresh?: boolean) {
    this.dialogRef.close(refresh);
  }

  ngOnDestroy() {
    if (this.ticketAttributeSubscription) {
      this.ticketAttributeSubscription.unsubscribe();
    }
    if (this.ticketAttributeValuesSubscription) {
      this.ticketAttributeValuesSubscription.unsubscribe();
    }
  }
}
